import React from 'react'
import { spacing } from '@mobi/component-library/Foundations'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { EmptyMessage } from '@core/Areas/BetLoop/Components/Common/EmptyMessage'
import { SkeletonLoader } from './Components/SkeletonLoader'
import { PendingTicket } from './Components/PendingTicket'
import { useSharePendingTicketsData } from './hooks/useSharePendingTicketsData'
import { useKambiSharePendingTicketsData } from './hooks/useKambiSharePendingTicketsData'
import { getSingularBetFromKambiCoupon } from '@core/Areas/BetLoop/Components/QuickShare/helpers/getSingularBetFromKambiCoupon'
import type { Ticket } from '@core/Areas/AccountActivity/types'
import type { KambiHistoryCoupon } from '@mobi/api-types/kambi'
import type { BetLoopKambiBetPayload } from '@core/Areas/BetLoop/helpers/api/types'
import { isVenueModeTicket } from '@core/Areas/VenueMode/Utils/isVenueModeTicket'
import { useVenueModeRacingEvents } from '@core/Areas/VenueMode/Components/Racing/Data/api'

export const ShareBetSelection: React.FC<{
  ticketsToShare: Set<number>
  kambiTicketsToShare: Set<BetLoopKambiBetPayload>
  onTicketClick(id: number, ticket: Ticket | KambiHistoryCoupon): void
  onEmptyState(): void
}> = ({ onTicketClick, ticketsToShare, kambiTicketsToShare, onEmptyState }) => {
  const {
    kambiTickets,
    isLoading: isKambiLoading,
    isEmptyState: isKambiEmptyState,
  } = useKambiSharePendingTicketsData()

  const {
    racingTickets,
    isLoading: isRacingLoading,
    isEmptyState: isRacingEmptyState,
  } = useSharePendingTicketsData()

  const isEmptyState = isRacingEmptyState && isKambiEmptyState
  const isLoading = isRacingLoading || isKambiLoading

  const { data: venueModeEvents } = useVenueModeRacingEvents()

  React.useEffect(() => {
    if (isEmptyState) onEmptyState()
  }, [isEmptyState, onEmptyState])

  if (isEmptyState) {
    return (
      <EmptyMessage
        messageType='tickets'
        title='No Eligible Pending Bets'
        description='Once you place a bet, it’ll show up here - and you’ll be able to share it to Bet Loop.'
      />
    )
  }

  return (
    <Flex flexDirection='column' gap={spacing.smx1}>
      {isLoading ? (
        <SkeletonLoader />
      ) : (
        <>
          {kambiTickets &&
            kambiTickets.map(ticket =>
              ticket.bets.map(bet => {
                if (bet.betStatus !== 'OPEN') return null
                return (
                  <PendingTicket
                    key={bet.betRef}
                    ticket={getSingularBetFromKambiCoupon(ticket, bet.betRef)}
                    isSelected={[...kambiTicketsToShare].some(x => x.betRef === bet.betRef)}
                    onClick={() => onTicketClick(bet.betRef, ticket)}
                  />
                )
              })
            )}

          {racingTickets &&
            racingTickets.map(ticket => {
              const ticketIsVenueMode = isVenueModeTicket(ticket, venueModeEvents)

              if (ticketIsVenueMode) {
                return null
              }

              return (
                <PendingTicket
                  key={ticket.ticketSequence}
                  ticket={ticket}
                  isSelected={ticketsToShare.has(ticket.ticketSequence)}
                  onClick={ticketSeq => onTicketClick(ticketSeq, ticket)}
                />
              )
            })}
        </>
      )}
    </Flex>
  )
}
